import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { AuthFacadeService } from '@features/auth/services/auth-facade.service';
import { NavItem } from '@layout/models/nav-item.model';

@Component({
  selector: 'app-menu',
  standalone: false,
  templateUrl: './menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    // `contents` keeps the <ul> a direct flex child of the sidebar column, so the
    // ancestor `group/nav` + `.is-collapsed` styles keep working unchanged.
    class: 'contents',
  },
})
export class MenuComponent {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthFacadeService);

  /** Sidebar rail state, owned by the shell. Drives what the rail keeps in the DOM. */
  readonly collapsed = input(false);

  /** Emitted when a nav item is clicked (the shell uses it to close the drawer). */
  readonly itemSelected = output<NavItem>();

  /** Current URL (without query/fragment), updated on every navigation. */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  readonly navItems: readonly NavItem[] = [
    {
      label: 'NAV.DASHBOARD',
      route: '/dashboard',
      icon: 'assets/icons/dashboard.png',
      exact: true,
    },
    { label: 'NAV.CUSTOMERS', route: '/dashboard/customers', icon: 'assets/icons/customer.png' },
    {
      label: 'NAV.VCR',
      route: '/dashboard/vcr',
      icon: 'assets/icons/vcr.png',
      hiddenFor: ['admin'],
    },
    {
      label: 'NAV.AUTHORIZATION',
      route: '/dashboard/authorization-decision',
      icon: 'assets/icons/authorization.png',
      hiddenFor: ['admin'],
    },
    {
      label: 'NAV.DOCUMENT_CENTER',
      route: '/dashboard/document-center',
      icon: 'assets/icons/document-center.png',
      hiddenFor: ['admin'],
    },
    {
      label: 'NAV.ALERT_CENTER',
      route: '/dashboard/alert-center',
      icon: 'assets/icons/alert-center.png',
      hiddenFor: ['admin'],
    },
    { label: 'NAV.REPORTS', route: '/dashboard/reports', icon: 'assets/icons/reports.png' },
    {
      label: 'NAV.AUDIT',
      route: '/dashboard/stock-history',
      icon: 'assets/icons/audit.png',
      hiddenFor: [],
    },
    {
      label: 'NAV.USERS',
      route: '/dashboard/manage-users',
      icon: 'assets/icons/users.png',
      hiddenFor: ['analyst', 'manager', 'coordinator'],
    },
  ];

  /** Access role of the authenticated user, lowercased so casing never matters. */
  private readonly role = computed(() => this.auth.vm().user?.role?.toLowerCase() ?? null);

  /**
   * Nav entries the current role is allowed to see. Hiding is opt-in per item, so
   * an unrecognized role - or no session yet - still gets the full menu.
   */
  readonly visibleNavItems = computed(() => {
    const role = this.role();
    if (role === null) {
      return this.navItems;
    }

    return this.navItems.filter((item) => !item.hiddenFor?.some((hidden) => hidden === role));
  });

  isActive(item: NavItem): boolean {
    const url = this.currentUrl().split(/[?#]/)[0];

    if (item.exact) {
      return url === item.route;
    }
    return url === item.route || url.startsWith(item.route + '/');
  }

  select(item: NavItem): void {
    this.itemSelected.emit(item);
  }
}
